import React, { memo, useCallback } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';

import { Tenant } from '../../types/tenant';
import { colors, typo } from '../../theme/tokens';

interface Props {
  tenant: Tenant;
  /** Rank in the recent-access list. -1 if not in list, 0 = most recent. */
  recentRank: number;
  onSelect: (tenant: Tenant) => void;
}

const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
};

const TenantCard: React.FC<Props> = memo(({ tenant, recentRank, onSelect }) => {
  const isRecent = recentRank !== -1;
  const isMostRecent = recentRank === 0;
  const initials = tenant.tenant_name?.charAt(0)?.toUpperCase() || 'T';
  const idDisplay = String(tenant.tenant_id || '—');
  const idLabel = idDisplay.length > 5 ? `${idDisplay.slice(0, 5)}...` : idDisplay;

  const handleClick = useCallback(() => onSelect(tenant), [onSelect, tenant]);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(tenant);
      }
    },
    [onSelect, tenant],
  );

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      aria-label={`Open ${tenant.tenant_name}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        p: 3,
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${colors.brand}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: 'rgba(250, 179, 6, 0.4)',
          borderLeftColor: colors.brand,
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          transform: 'translateY(-3px)',
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.brand}`,
          outlineOffset: 2,
        },
      }}
    >
      {isRecent && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: '6px',
            bgcolor: isMostRecent ? colors.brandLight : colors.bgMuted,
            border: `1px solid ${isMostRecent ? 'rgba(250,179,6,0.2)' : colors.borderLight}`,
          }}
        >
          <HistoryIcon
            sx={{ fontSize: 12, color: isMostRecent ? colors.brand : colors.textTertiary }}
          />
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              fontFamily: 'Manrope, sans-serif',
              color: isMostRecent ? colors.brandHover : colors.textTertiary,
            }}
          >
            {isMostRecent ? 'Last used' : 'Recent'}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: 44,
          height: 44,
          bgcolor: colors.brand,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          color: '#FFFFFF',
          fontSize: 18,
          fontWeight: 700,
          fontFamily: 'Manrope, sans-serif',
          boxShadow: '0 2px 8px rgba(250, 179, 6, 0.3)',
        }}
      >
        {initials}
      </Box>

      <Typography
        sx={{
          ...typo.sectionTitle,
          mb: 1,
          lineHeight: 1.3,
          minHeight: 42,
        }}
      >
        {tenant.tenant_name}
      </Typography>

      {tenant.is_prod_tenant && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            mb: 1,
            px: 0.75,
            py: 0.15,
            borderRadius: '6px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.4,
            color: colors.warningText,
            bgcolor: colors.warningBg,
            border: `1px solid ${colors.warningBorder}`,
          }}
        >
          PROD
        </Box>
      )}

      {/* Synthetic (test) tenant — same slot and shape as PROD, in the info blue
          the tenant page's Synthetic badge uses (MNS-1276). */}
      {tenant.is_synthetic && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            mb: 1,
            ml: tenant.is_prod_tenant ? 0.75 : 0,
            px: 0.75,
            py: 0.15,
            borderRadius: '6px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.4,
            color: colors.infoText,
            bgcolor: colors.infoBg,
            border: `1px solid ${colors.infoBorder}`,
          }}
        >
          Synthetic
        </Box>
      )}

      <Box>
        <Typography sx={typo.smallText}>Tenant ID: {idLabel}</Typography>
        <Typography sx={typo.smallText}>Created: {fmtDate(tenant.versioned_at)}</Typography>
      </Box>
    </Paper>
  );
});

TenantCard.displayName = 'TenantCard';

export default TenantCard;
